// @flow
import * as React from 'react'
import * as topojson from 'topojson-client'
import config from 'config'
import type { GeojsonFeature } from 'components/map/component'

type MapProps = {
  canHover: boolean,
  canTouch: boolean,
  geometryCollection: { type: string, geometries: Array<GeojsonFeature> },
  mapId: string,
  mouseOverHandler: (id: number, event: SyntheticEvent<SVGPathElement>) => void,
  pathGenerator: GeojsonFeature => string
}

const kreisFeatures: Array<GeojsonFeature> = topojson.feature(
  config.schweiz,
  config.zuercherKreise
).features

/**
 * Draw a choropleth map
 * @param {object} props
 * @param props.gemeinden
 * @param props.mouseOverHandler
 * @constructor
 */
export function EventListenerMap ({
  canHover,
  canTouch,
  geometryCollection,
  mapId = '',
  mouseOverHandler,
  pathGenerator
}: MapProps): React.Element<'g'> | null {
  const gemeindeFeatures: Array<GeojsonFeature> = topojson.feature(
    config.schweiz,
    geometryCollection
  ).features

  function drawSwissMunicipalities (
    attachMouseHandler: boolean,
    areas: Array<GeojsonFeature>,
    areaIdProperty = 'GMDNR',
    idGenerator = property => property
  ): Array<React.Element<'path' | 'g'>> {
    return areas.map((feature: GeojsonFeature, index: number) => {
      const municipalityId = idGenerator(+feature.properties[areaIdProperty])

      let interactionHandlers = {}

      if (attachMouseHandler && municipalityId !== 261) {
        let boundMouseOverHandler = event =>
          mouseOverHandler(+municipalityId, event)
        interactionHandlers = {
          onMouseOver: canHover ? boundMouseOverHandler : undefined,
          onTouchStart: canTouch ? boundMouseOverHandler : undefined
        }
      }

      let colouredMunicipalityPath = (
        <path
          key={municipalityId}
          d={pathGenerator(feature)}
          id={`${mapId}-gemeinde-${municipalityId}`}
          {...interactionHandlers}
          className='gemeinde'
          fill='transparent'
        />
      )

      return colouredMunicipalityPath
    })
  }

  const municipalityPaths = drawSwissMunicipalities(true, gemeindeFeatures)
  const zuerichKreise = drawSwissMunicipalities(
    true,
    kreisFeatures,
    'knr',
    id => `261${id.toString().padStart(2, '0')}`
  )
  const partsPaths = municipalityPaths.concat(zuerichKreise)

  return <g className='event listening municipalities'>{partsPaths}</g>
}
